'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Moon, Sun, Bell, LogOut, User } from 'lucide-react';
import { toggleTheme, getTheme } from '@/lib/theme';
import { authApi, usersApi, tokenStore } from '@/lib/api';
import { useEffect, useState } from 'react';

export function Header({ title }: { title?: string }) {
  const router = useRouter();
  const [theme, setTheme] = useState<'light' | 'dark'>('light');
  const [displayName, setDisplayName] = useState<string | null>(null);

  useEffect(() => {
    setTheme(getTheme());
    usersApi.me().then((data) => {
      setDisplayName(data.profile.fullName ?? data.email);
    }).catch(() => {/* silently ignore — header is non-critical */});
  }, []);

  function handleToggleTheme() {
    const next = toggleTheme();
    setTheme(next);
  }

  async function handleLogout() {
    try {
      await authApi.logout();
    } finally {
      tokenStore.clear();
      router.push('/login');
    }
  }

  return (
    <header
      className="flex items-center justify-between px-6 h-[var(--header-height)] shrink-0 border-b border-[var(--color-border)] bg-[var(--color-bg-base)] sticky top-0 z-[var(--z-sticky)]"
    >
      {/* Page title slot */}
      <div>
        {title && (
          <h1 className="text-[length:var(--text-lg)] font-[var(--weight-semibold)] text-[var(--color-text-primary)]">
            {title}
          </h1>
        )}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-1">
        {/* Theme toggle */}
        <button
          onClick={handleToggleTheme}
          aria-label="Toggle theme"
          className="w-9 h-9 flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors duration-[var(--duration-fast)]"
        >
          {theme === 'dark' ? <Sun size={16} strokeWidth={1.75} /> : <Moon size={16} strokeWidth={1.75} />}
        </button>

        {/* Notifications */}
        <button
          aria-label="Notifications"
          className="w-9 h-9 flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors duration-[var(--duration-fast)]"
        >
          <Bell size={16} strokeWidth={1.75} />
        </button>

        <div className="w-px h-5 bg-[var(--color-border)] mx-1" />

        {/* Profile */}
        <Link
          href="/settings"
          aria-label="Settings"
          className="flex items-center gap-2 px-2 h-9 rounded-[var(--radius-md)] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-elevated)] transition-colors duration-[var(--duration-fast)]"
        >
          <User size={16} strokeWidth={1.75} />
          {displayName && (
            <span className="text-[length:var(--text-sm)] font-[var(--weight-medium)] max-w-[120px] truncate">
              {displayName}
            </span>
          )}
        </Link>

        {/* Logout */}
        <button
          onClick={handleLogout}
          aria-label="Sign out"
          className="w-9 h-9 flex items-center justify-center rounded-[var(--radius-md)] text-[var(--color-text-secondary)] hover:text-[var(--color-error-text)] hover:bg-[var(--color-error-bg)] transition-colors duration-[var(--duration-fast)]"
        >
          <LogOut size={16} strokeWidth={1.75} />
        </button>
      </div>
    </header>
  );
}
